import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '@/utils/api';

/**
 * Agent registration thunks (ADR-011 Phase 2b).
 *
 * Three entry points cover the managed-custody surface:
 *
 *   registerManagedAgent          → POST /agents/register
 *                                   Subsidy path (Klyx pays init fee).
 *                                   Discriminated 501 rejection surfaces
 *                                   subsidy-unavailable to trigger the
 *                                   owner-paid fallback below.
 *
 *   registerAgentSelfFund         → POST /agents/register/self-fund
 *                                   Owner-paid one-shot for EMAIL-auth
 *                                   owners. Server signs the KLV
 *                                   Transfer from the owner's custodied
 *                                   wallet, then mints. 402 signals
 *                                   insufficient KLV in that wallet.
 *
 *   initOwnerPaidAgent            → POST /agents/register/init
 *                                   Owner-paid step 1 for WALLET-auth
 *                                   owners. Provisions agent shell,
 *                                   returns wallet address + required
 *                                   KLV amount. Frontend then uses
 *                                   buildSignBroadcast to sign a
 *                                   Transfer via Klever extension.
 *
 *   completeOwnerPaidAgent        → POST /agents/register/complete
 *                                   Owner-paid step 2 for wallet users.
 *                                   Server verifies the funding tx and
 *                                   mints. Idempotent.
 *
 * Self-custody import (owner brings their own private key) lands in
 * Phase 3 as a separate thunk.
 */

// ------------------------------------------------------------------ types

export interface RegisterManagedAgentBody {
  displayName: string;
  bio?: string;
  avatarFile?: File | null;
  /** Ring 2 capability declarations. Server validates each value
   *  against AGENT_CAPABILITIES; empty/omitted is allowed. */
  capabilities?: string[];
  /** Chains the agent operates on. Server validates each value
   *  against AGENT_CHAINS; empty/omitted is allowed. */
  chains?: string[];
}

export interface RegisteredAgent {
  userId: string;
  walletAddress: string;
  profileId: number;
  sftNonce: number;
}

export interface RegisterManagedAgentResponse {
  success: boolean;
  agent: RegisteredAgent;
  txHash: string;
}

export interface InitOwnerPaidResponse {
  success: boolean;
  agentUserId: string;
  agentWalletAddress: string;
  requiredKlv: {
    smallest: string;
    display: string;
    token: 'KLV';
  };
  next?: { step: string; instructions: string };
}

export interface RequiredKlv {
  smallest: string;
  display: string;
  token: 'KLV';
}

export type RegisterManagedAgentRejection =
  | {
      kind: 'subsidy_unavailable';
      reason: 'disabled' | 'global_limit_reached' | 'once_per_owner_used';
      message: string;
      // Enriched from the /register 501 response so the confirm panel
      // can show the accurate fee without a follow-up call.
      requiredKlv?: RequiredKlv;
    }
  | { kind: 'display_name_taken'; message: string }
  | { kind: 'error'; message: string };

export type RegisterAgentSelfFundRejection =
  | { kind: 'insufficient_funds'; requiredKlv: string; availableKlv: string; message: string }
  | { kind: 'not_email_owner'; message: string; hint?: string }
  | { kind: 'display_name_taken'; message: string }
  | { kind: 'error'; message: string };

export type CompleteOwnerPaidRejection =
  | { kind: 'funding_verification'; message: string }
  | { kind: 'ownership'; message: string }
  | { kind: 'error'; message: string };

export type AgentEndpointMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface AgentEndpoint {
  id: string;
  label: string | null;
  url: string;
  description: string | null;
  method: AgentEndpointMethod;
  /** Smallest-unit decimal string. '0' + '0' → Free per
   *  formatEndpointPrice. Server enforces max >= min. */
  priceMinSmallest: string;
  priceMaxSmallest: string;
  /** Uppercase ticker. Server validates against AGENT_CHAINS on
   *  write; unknown values won't come back on read. */
  priceAsset: string;
  createdAt: string;
}

/**
 * Per-chain wallet row. Server returns one primary per chain via the
 * partial-unique index on (agent_user_id, chain) WHERE is_primary.
 */
export interface AgentWallet {
  id: string;
  agentUserId: string;
  chain: string;
  address: string;
  isPrimary: boolean;
  label: string | null;
  createdAt: string;
}

export interface OwnedAgent {
  userId: string;
  username: string;
  walletAddress: string | null;
  custodyMode: 'managed' | 'self_custody';
  capabilities: string[];
  /** Chains the agent operates on — same story as capabilities:
   *  always an array, empty when nothing was declared. */
  chains: string[];
  /** Ring 2.5 endpoints — always an array; empty when the owner
   *  hasn't declared any yet. */
  endpoints: AgentEndpoint[];
  /** Per-chain wallets (x402-style). At most one primary per chain
   *  (server enforces via partial-unique index). Empty for freshly
   *  registered agents that haven't added any yet. */
  wallets: AgentWallet[];
  /** Ring 3 Phase 1: raw receipt count across all lifecycle states.
   *  Phase 2 will add a matured-only score alongside. */
  receiptCount: number;
  /** Issue #661: count of accepted (asset, chain) payment lanes.
   *  Powers the "N lanes" badge on the My Agents list. */
  laneCount: number;
  /** ADR-012 public verification list (present on Discover as of
   *  PR H; empty for agents without any verifications). Email
   *  targetValue is domain-only; endpoint/DNS targetValue is the
   *  raw URL/hostname. Revoked rows already filtered server-side. */
  verifications?: Array<{
    id: string;
    type: 'email' | 'endpoint' | 'dns_txt';
    targetValue: string;
    status: 'pending' | 'verified' | 'stale';
    verifiedAt: string | null;
    staleSince: string | null;
  }>;
  /** ADR-015 D8 platform-run flag. True only for the small set of
   *  Klyx-operated agents (@klyx-discovery, @klyx-audit). Drives the
   *  "Operated by Klyx" chip on cards + profile hero. */
  isKlyxOperated: boolean;
  createdAt: string;
  displayName: string | null;
  avatarUrl: string | null;
  bio: string | null;
  profileId: number | null;
  sftNonce: number | null;
  /** app.nft_profile.id UUID — the DB primary key, not the on-chain
   *  SFT nonce (which lives in `profileId`). Needed to invoke
   *  downstream flows that key off nft_profile.id — e.g. the
   *  spotlight purchase endpoints. Null when the agent hasn't
   *  completed on-chain registration + minting yet. */
  nftProfileUuid: string | null;
}

/**
 * Ring 3 Phase 1 — receipt row shape as the server returns it
 * (mirror of AgentReceiptDto). paymentAmountSmallest is a string
 * because numeric(78,0) exceeds JS Number precision.
 */
export type AgentReceiptOutcome =
  | 'completed'
  | 'failed'
  | 'timed_out'
  | 'disputed';

export type AgentReceiptState =
  | 'pending_sigs'
  | 'signed'
  | 'settled'
  | 'matured'
  | 'disputed'
  | 'resolved_completed'
  | 'resolved_failed';

export interface AgentReceiptDto {
  id: string;
  schemaVersion: number;
  requesterWallet: string | null;
  requesterAgentUserId: string | null;
  /** Hydrated by the server (LEFT JOIN app_user). Null when requester
   *  was a human wallet or when the requester agent was deleted after
   *  the receipt landed. */
  requesterAgentUsername: string | null;
  providerAgentUserId: string;
  /** Hydrated by the server. Non-null in practice (provider FK is
   *  NOT NULL + ON DELETE CASCADE removes the receipt), but typed
   *  nullable to keep the join-not-found path honest. */
  providerAgentUsername: string | null;
  providerEndpointId: string | null;
  capability: string | null;
  paymentAmountSmallest: string;
  paymentAsset: string;
  paymentTxHash: string | null;
  outcome: AgentReceiptOutcome;
  inputHash: string | null;
  outputHash: string | null;
  invokedAt: string;
  completedAt: string | null;
  state: AgentReceiptState;
  requesterSignature: { signature: string; keyVersion: number } | null;
  providerSignature: { signature: string; keyVersion: number } | null;
  nonce: string;
  metadata: Record<string, unknown>;
  chainRef: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

/** Ring 2.5 — the edit-scoped shape returned by /agents/mine/:id.
 *  Slimmer than OwnedAgent (no on-chain profileId/sftNonce yet). */
export interface OwnedAgentDetail {
  agent: {
    userId: string;
    displayName: string | null;
    bio: string | null;
    avatarUrl: string | null;
    capabilities: string[];
    chains: string[];
    isKlyxOperated: boolean;
    username: string;
    walletAddress: string | null;
  };
  endpoints: AgentEndpoint[];
  wallets: AgentWallet[];
  /** Per-agent (asset, chain) payment lanes — see issue #661. */
  acceptedSettlements: AgentAcceptedSettlement[];
  /**
   * (asset, chain) tuples the platform CAN currently route today.
   * Matrix cells for tuples NOT in this list get greyed + disabled —
   * they'd 400 at save time otherwise (see #667 + #666).
   */
  platformServableLanes: Array<{ asset: string; chain: string }>;
}

/** One accepted payment lane. Endpoint pricing stays symbol-only;
 *  this tuple decides which chains the symbol fans out to at 402
 *  handshake time. */
export interface AgentAcceptedSettlement {
  asset: string;
  chain: string;
  createdAt: string;
}

export interface UpdateAgentPayload {
  agentUserId: string;
  patch: {
    displayName?: string;
    bio?: string | null;
    avatarUrl?: string | null;
    capabilities?: string[];
    chains?: string[];
  };
}

export interface AddAgentEndpointPayload {
  agentUserId: string;
  endpoint: {
    label?: string | null;
    url: string;
    description?: string | null;
    method: AgentEndpointMethod;
    priceMinSmallest?: string;
    priceMaxSmallest?: string;
    priceAsset?: string;
  };
}

export interface DeleteAgentEndpointPayload {
  agentUserId: string;
  endpointId: string;
}

// Discover surface uses the same row shape as MyAgents — the
// server returns AgentDetailsRow from both endpoints.
export type DiscoverableAgent = OwnedAgent;

export interface DiscoverAgentsResponse {
  agents: DiscoverableAgent[];
  nextCursor: string | null;
  /** Total agents matching the filter (capability + chain), ignoring
   *  the cursor. Present only on first-page loads (no cursor); the
   *  slice caches the value across subsequent Load-more calls since
   *  the total doesn't change between pages of the same filter. */
  total?: number;
  /** Currently-active global-scope agent spotlights (up to 5).
   *  Present only on first-page loads; slice caches across Load-more
   *  calls since active spotlights don't change per page. Same
   *  hydrated shape as `agents` so the same card component renders. */
  spotlights?: DiscoverableAgent[];
}

export interface DiscoverAgentsArgs {
  capabilities?: string[];
  chains?: string[];
  cursor?: string | null;
  limit?: number;
}

// ------------------------------------------------------------------ helpers

/**
 * Build the multipart body shared by /register and /register/self-fund.
 * Kept as a helper so a body-shape change (new field, renamed one)
 * doesn't drift between the two thunks.
 */
function buildRegisterFormData(body: RegisterManagedAgentBody): FormData {
  const formData = new FormData();
  formData.append('displayName', body.displayName);
  if (body.bio && body.bio.trim().length > 0) {
    formData.append('bio', body.bio.trim());
  }
  if (body.avatarFile) {
    formData.append('avatar', body.avatarFile);
  }
  // multipart/form-data can't natively carry arrays, so serialize.
  // The server's RegisterAgentSchema (capabilitiesSchema) accepts
  // either a real array or a JSON string and parses accordingly.
  if (body.capabilities && body.capabilities.length > 0) {
    formData.append('capabilities', JSON.stringify(body.capabilities));
  }
  // Same JSON-string trick as capabilities. chainsSchema on the
  // server double-decodes.
  if (body.chains && body.chains.length > 0) {
    formData.append('chains', JSON.stringify(body.chains));
  }
  return formData;
}

// ------------------------------------------------------------------ subsidy

export const registerManagedAgent = createAsyncThunk<
  RegisterManagedAgentResponse,
  RegisterManagedAgentBody,
  { rejectValue: RegisterManagedAgentRejection }
>('agent/registerManagedAgent', async (body, { rejectWithValue }) => {
  try {
    const response = await api.post<RegisterManagedAgentResponse>(
      '/agents/register',
      buildRegisterFormData(body),
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  } catch (error: any) {
    const status = error?.response?.status;
    const data = error?.response?.data;
    if (status === 501 && data?.reason) {
      return rejectWithValue({
        kind: 'subsidy_unavailable',
        reason: data.reason,
        message: data.error || 'Agent init subsidy unavailable',
        requiredKlv: data.requiredKlv,
      });
    }
    if (status === 409 && data?.field === 'displayName') {
      return rejectWithValue({ kind: 'display_name_taken', message: data.error });
    }
    return rejectWithValue({
      kind: 'error',
      message: data?.error || data?.message || 'Failed to register agent',
    });
  }
});

// ------------------------------------------------------------------ owner-paid (email)

export const registerAgentSelfFund = createAsyncThunk<
  RegisterManagedAgentResponse,
  RegisterManagedAgentBody,
  { rejectValue: RegisterAgentSelfFundRejection }
>('agent/registerAgentSelfFund', async (body, { rejectWithValue }) => {
  try {
    const response = await api.post<RegisterManagedAgentResponse>(
      '/agents/register/self-fund',
      buildRegisterFormData(body),
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  } catch (error: any) {
    const status = error?.response?.status;
    const data = error?.response?.data;
    if (status === 402 && data?.requiredKlv !== undefined) {
      return rejectWithValue({
        kind: 'insufficient_funds',
        requiredKlv: data.requiredKlv,
        availableKlv: data.availableKlv,
        message: data.error || 'Not enough KLV in your Klyx wallet',
      });
    }
    if (status === 400 && data?.hint) {
      return rejectWithValue({
        kind: 'not_email_owner',
        message: data.error,
        hint: data.hint,
      });
    }
    if (status === 409 && data?.field === 'displayName') {
      return rejectWithValue({ kind: 'display_name_taken', message: data.error });
    }
    return rejectWithValue({
      kind: 'error',
      message: data?.error || data?.message || 'Failed to register agent',
    });
  }
});

// ------------------------------------------------------------------ owner-paid (wallet) — two-step

export const initOwnerPaidAgent = createAsyncThunk<
  InitOwnerPaidResponse,
  RegisterManagedAgentBody,
  { rejectValue: RegisterManagedAgentRejection }
>('agent/initOwnerPaidAgent', async (body, { rejectWithValue }) => {
  try {
    const response = await api.post<InitOwnerPaidResponse>(
      '/agents/register/init',
      buildRegisterFormData(body),
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  } catch (error: any) {
    const status = error?.response?.status;
    const data = error?.response?.data;
    if (status === 409 && data?.field === 'displayName') {
      return rejectWithValue({ kind: 'display_name_taken', message: data.error });
    }
    return rejectWithValue({
      kind: 'error',
      message: data?.error || data?.message || 'Failed to initialize agent registration',
    });
  }
});

export interface CompleteOwnerPaidPayload {
  agentUserId: string;
  fundingTxHash: string;
}

/**
 * Fetch the caller's registered agents for the "My agents" page.
 * Server returns them newest-first with enough joined-in fields
 * that we render each row without follow-up per-agent calls.
 */
export const fetchOwnerAgents = createAsyncThunk<
  { agents: OwnedAgent[] },
  void,
  { rejectValue: { message: string } }
>('agent/fetchOwnerAgents', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get<{ agents: OwnedAgent[] }>('/agents/mine');
    return response.data;
  } catch (error: any) {
    return rejectWithValue({
      message:
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        'Failed to load your agents',
    });
  }
});

/**
 * Ring 2 — public discover browse. Passes any active capability
 * filter as repeated ?capability= params and (optionally) a cursor
 * for the next page. Reducer decides whether to replace the list
 * (first page / filter change) or append (Load more) based on
 * whether the request carried a cursor.
 */
// Ring 2.5 — owner-scoped detail fetch for the edit page.
export const fetchOwnedAgentDetail = createAsyncThunk<
  OwnedAgentDetail,
  string,
  { rejectValue: { message: string; status?: number } }
>('agent/fetchOwnedAgentDetail', async (agentUserId, { rejectWithValue }) => {
  try {
    const response = await api.get<OwnedAgentDetail>(`/agents/mine/${agentUserId}`);
    return response.data;
  } catch (error: any) {
    return rejectWithValue({
      message: error?.response?.data?.error || 'Failed to load agent',
      status: error?.response?.status,
    });
  }
});

export type UpdateAgentRejection =
  | { kind: 'display_name_taken'; message: string }
  | { kind: 'not_found'; message: string }
  | { kind: 'error'; message: string };

export const updateOwnedAgent = createAsyncThunk<
  OwnedAgentDetail,
  UpdateAgentPayload,
  { rejectValue: UpdateAgentRejection }
>('agent/updateOwnedAgent', async ({ agentUserId, patch }, { rejectWithValue }) => {
  try {
    const response = await api.patch<OwnedAgentDetail>(
      `/agents/mine/${agentUserId}`,
      patch,
    );
    return response.data;
  } catch (error: any) {
    const status = error?.response?.status;
    const data = error?.response?.data;
    if (status === 409 && data?.field === 'displayName') {
      return rejectWithValue({ kind: 'display_name_taken', message: data.error });
    }
    if (status === 404) {
      return rejectWithValue({ kind: 'not_found', message: data?.error || 'Agent not found' });
    }
    return rejectWithValue({
      kind: 'error',
      message: data?.error || 'Failed to update agent',
    });
  }
});

export type AddEndpointRejection =
  | { kind: 'duplicate'; message: string }
  | { kind: 'error'; message: string };

export const addOwnedAgentEndpoint = createAsyncThunk<
  { endpoint: AgentEndpoint; agentUserId: string },
  AddAgentEndpointPayload,
  { rejectValue: AddEndpointRejection }
>('agent/addOwnedAgentEndpoint', async ({ agentUserId, endpoint }, { rejectWithValue }) => {
  try {
    const response = await api.post<{ endpoint: AgentEndpoint }>(
      `/agents/mine/${agentUserId}/endpoints`,
      endpoint,
    );
    return { endpoint: response.data.endpoint, agentUserId };
  } catch (error: any) {
    const status = error?.response?.status;
    const data = error?.response?.data;
    if (status === 409) {
      return rejectWithValue({
        kind: 'duplicate',
        message: data?.error || 'That endpoint is already declared',
      });
    }
    return rejectWithValue({
      kind: 'error',
      message: data?.error || 'Failed to add endpoint',
    });
  }
});

export interface UpdateAgentEndpointPayload {
  agentUserId: string;
  endpointId: string;
  patch: {
    label?: string | null;
    url?: string;
    description?: string | null;
    method?: AgentEndpointMethod;
    priceMinSmallest?: string;
    priceMaxSmallest?: string;
    priceAsset?: string;
  };
}

export const updateOwnedAgentEndpoint = createAsyncThunk<
  { endpoint: AgentEndpoint; agentUserId: string },
  UpdateAgentEndpointPayload,
  { rejectValue: AddEndpointRejection }
>('agent/updateOwnedAgentEndpoint', async ({ agentUserId, endpointId, patch }, { rejectWithValue }) => {
  try {
    const response = await api.patch<{ endpoint: AgentEndpoint }>(
      `/agents/mine/${agentUserId}/endpoints/${endpointId}`,
      patch,
    );
    return { endpoint: response.data.endpoint, agentUserId };
  } catch (error: any) {
    const status = error?.response?.status;
    const data = error?.response?.data;
    if (status === 409) {
      return rejectWithValue({
        kind: 'duplicate',
        message: data?.error || 'That endpoint URL + method is already declared',
      });
    }
    return rejectWithValue({
      kind: 'error',
      message: data?.error || 'Failed to update endpoint',
    });
  }
});

export const deleteOwnedAgentEndpoint = createAsyncThunk<
  DeleteAgentEndpointPayload,
  DeleteAgentEndpointPayload,
  { rejectValue: { message: string } }
>('agent/deleteOwnedAgentEndpoint', async (payload, { rejectWithValue }) => {
  try {
    await api.delete(
      `/agents/mine/${payload.agentUserId}/endpoints/${payload.endpointId}`,
    );
    return payload;
  } catch (error: any) {
    return rejectWithValue({
      message: error?.response?.data?.error || 'Failed to delete endpoint',
    });
  }
});

// -------------------------------------------------------- Wallet thunks

export interface AddAgentWalletPayload {
  agentUserId: string;
  wallet: {
    chain: string;
    address: string;
    isPrimary?: boolean;
    label?: string | null;
  };
}

export type AddAgentWalletRejection =
  | { kind: 'duplicate'; message: string }
  | { kind: 'invalid'; message: string }
  | { kind: 'not-found'; message: string }
  | { kind: 'server'; message: string };

export const addOwnedAgentWallet = createAsyncThunk<
  { agentUserId: string; wallet: AgentWallet },
  AddAgentWalletPayload,
  { rejectValue: AddAgentWalletRejection }
>('agent/addOwnedAgentWallet', async (payload, { rejectWithValue }) => {
  try {
    const response = await api.post<{ wallet: AgentWallet }>(
      `/agents/mine/${payload.agentUserId}/wallets`,
      payload.wallet,
    );
    return { agentUserId: payload.agentUserId, wallet: response.data.wallet };
  } catch (error: any) {
    const status = error?.response?.status;
    const data = error?.response?.data;
    if (status === 409) {
      return rejectWithValue({
        kind: 'duplicate',
        message: data?.error || 'That address is already registered on this chain',
      });
    }
    if (status === 400) {
      return rejectWithValue({
        kind: 'invalid',
        message: data?.error || 'Invalid wallet',
      });
    }
    if (status === 404) {
      return rejectWithValue({
        kind: 'not-found',
        message: data?.error || 'Agent not found',
      });
    }
    return rejectWithValue({
      kind: 'server',
      message: data?.error || 'Failed to add wallet',
    });
  }
});

export const setPrimaryOwnedAgentWallet = createAsyncThunk<
  { agentUserId: string; wallet: AgentWallet },
  { agentUserId: string; walletId: string },
  { rejectValue: { message: string } }
>(
  'agent/setPrimaryOwnedAgentWallet',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post<{ wallet: AgentWallet }>(
        `/agents/mine/${payload.agentUserId}/wallets/${payload.walletId}/primary`,
      );
      return { agentUserId: payload.agentUserId, wallet: response.data.wallet };
    } catch (error: any) {
      return rejectWithValue({
        message: error?.response?.data?.error || 'Failed to promote wallet',
      });
    }
  },
);

export const deleteOwnedAgentWallet = createAsyncThunk<
  { agentUserId: string; walletId: string },
  { agentUserId: string; walletId: string },
  { rejectValue: { message: string } }
>(
  'agent/deleteOwnedAgentWallet',
  async (payload, { rejectWithValue }) => {
    try {
      await api.delete(
        `/agents/mine/${payload.agentUserId}/wallets/${payload.walletId}`,
      );
      return payload;
    } catch (error: any) {
      return rejectWithValue({
        message: error?.response?.data?.error || 'Failed to delete wallet',
      });
    }
  },
);

// -------------------------------------------------- Accepted lanes (#661)

export interface AddAgentAcceptedSettlementPayload {
  agentUserId: string;
  asset: string;
  chain: string;
}

export const addOwnedAgentAcceptedSettlement = createAsyncThunk<
  { agentUserId: string; lane: AgentAcceptedSettlement },
  AddAgentAcceptedSettlementPayload,
  { rejectValue: { message: string } }
>(
  'agent/addOwnedAgentAcceptedSettlement',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post<{ lane: AgentAcceptedSettlement }>(
        `/agents/mine/${payload.agentUserId}/accepted-settlements`,
        { asset: payload.asset, chain: payload.chain },
      );
      return { agentUserId: payload.agentUserId, lane: response.data.lane };
    } catch (error: any) {
      return rejectWithValue({
        message:
          error?.response?.data?.error || 'Failed to add accepted settlement',
      });
    }
  },
);

export const deleteOwnedAgentAcceptedSettlement = createAsyncThunk<
  { agentUserId: string; asset: string; chain: string },
  { agentUserId: string; asset: string; chain: string },
  { rejectValue: { message: string } }
>(
  'agent/deleteOwnedAgentAcceptedSettlement',
  async (payload, { rejectWithValue }) => {
    try {
      await api.delete(
        `/agents/mine/${payload.agentUserId}/accepted-settlements/${payload.asset}/${payload.chain}`,
      );
      return payload;
    } catch (error: any) {
      return rejectWithValue({
        message:
          error?.response?.data?.error || 'Failed to delete accepted settlement',
      });
    }
  },
);

export const fetchDiscoverAgents = createAsyncThunk<
  DiscoverAgentsResponse & { appendedToPrevious: boolean },
  DiscoverAgentsArgs,
  { rejectValue: { message: string } }
>('agent/fetchDiscoverAgents', async (args, { rejectWithValue }) => {
  try {
    const params = new URLSearchParams();
    (args.capabilities ?? []).forEach((c) => params.append('capability', c));
    (args.chains ?? []).forEach((c) => params.append('chain', c));
    if (args.cursor) params.append('cursor', args.cursor);
    if (args.limit) params.append('limit', String(args.limit));
    const response = await api.get<DiscoverAgentsResponse>(
      `/agents/discover?${params.toString()}`,
    );
    return {
      ...response.data,
      appendedToPrevious: !!args.cursor,
    };
  } catch (error: any) {
    return rejectWithValue({
      message:
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        'Failed to load agents',
    });
  }
});

export const completeOwnerPaidAgent = createAsyncThunk<
  RegisterManagedAgentResponse,
  CompleteOwnerPaidPayload,
  { rejectValue: CompleteOwnerPaidRejection }
>('agent/completeOwnerPaidAgent', async (payload, { rejectWithValue }) => {
  try {
    // metadataUri intentionally omitted — server auto-resolves it from
    // the agentUserId (same URI it would have generated on init).
    const response = await api.post<RegisterManagedAgentResponse>(
      '/agents/register/complete',
      payload,
    );
    return response.data;
  } catch (error: any) {
    const status = error?.response?.status;
    const data = error?.response?.data;
    if (status === 400) {
      return rejectWithValue({
        kind: 'funding_verification',
        message: data?.error || 'Funding transaction could not be verified',
      });
    }
    if (status === 403) {
      return rejectWithValue({
        kind: 'ownership',
        message: data?.error || 'Not authorized to complete this agent',
      });
    }
    return rejectWithValue({
      kind: 'error',
      message: data?.error || data?.message || 'Failed to complete agent registration',
    });
  }
});

// -------------------------------------------------------- Receipts (Ring 3 Phase 1)

export interface FetchAgentReceiptsArgs {
  providerAgentUserId: string;
  cursor?: string | null;
  limit?: number;
}

export interface FetchAgentReceiptsResponse {
  receipts: AgentReceiptDto[];
  nextCursor: string | null;
  appendedToPrevious: boolean;
}

/**
 * List receipts for a given provider (agent), newest first. Cursor
 * pagination mirrors /agents/discover — the reducer distinguishes
 * first-page (replace) from subsequent (append) via
 * `appendedToPrevious`.
 */
export const fetchAgentReceipts = createAsyncThunk<
  FetchAgentReceiptsResponse,
  FetchAgentReceiptsArgs,
  { rejectValue: { message: string } }
>('agent/fetchAgentReceipts', async (args, { rejectWithValue }) => {
  try {
    const params = new URLSearchParams();
    params.append('providerAgentUserId', args.providerAgentUserId);
    if (args.cursor) params.append('cursor', args.cursor);
    if (args.limit) params.append('limit', String(args.limit));
    const response = await api.get<{
      receipts: AgentReceiptDto[];
      nextCursor: string | null;
    }>(`/agents/receipts?${params.toString()}`);
    return {
      receipts: response.data.receipts,
      nextCursor: response.data.nextCursor,
      appendedToPrevious: !!args.cursor,
    };
  } catch (error: any) {
    return rejectWithValue({
      message:
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        'Failed to load receipts',
    });
  }
});
